/**
 * @file Components/Booking/BookingForm.jsx
 * @description Tour booking form component. Handles date selection, guest count,
 * special requests, pricing calculation, and booking submission via the API.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getTour, createBooking } from '../../services/api';
import { useAuth } from '../../Context/Auth';
import { format } from 'date-fns';

const BookingForm = () => {
  const { id: tourId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    tour: tourId || '',
    tourDate: '',
    numberOfPeople: 1,
    specialRequests: '',
    contactNumber: ''
  });
  
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);

  const location = useLocation();

  useEffect(() => {
    const fetchTour = async () => {
      try {
        const { data } = await getTour(tourId);
        setTour(data);
        setFormData(prev => ({
          ...prev,
          tour: data._id,
          price: data.price
        }));
        setTotalPrice(data.price);
      } catch (error) {
        console.error('Error fetching tour:', error);
        toast.error(error.response?.data?.message || 'Failed to load tour details');
        navigate('/tours');
      } finally {
        setLoading(false);
      }
    };

    if (tourId) {
      fetchTour();
    } else {
      setLoading(false);
    }
  }, [tourId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'numberOfPeople' ? parseInt(value) : value
    }));

    if (name === 'numberOfPeople' && tour) {
      setTotalPrice(tour.price * value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    if (!user) {
      toast.error('Please log in to book a tour');
      navigate('/login', { 
        state: { 
          from: {
            pathname: `/tours/${tourId}/book`,
            search: location.search
          } 
        } 
      });
      return;
    }

    try {
      const bookingData = {
        tour: tourId,
        date: formData.tourDate,
        participants: formData.numberOfPeople,
        specialRequests: formData.specialRequests,
        contactNumber: formData.contactNumber,
        totalAmount: totalPrice
      };
      
      await createBooking(bookingData);
      toast.success('Booking successful! You will receive a confirmation email shortly.');
      navigate('/my-bookings');
    } catch (error) {
      console.error('Error creating booking:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create booking. Please try again.';
      toast.error(errorMessage);
      
      if (error.response?.status === 401) {
        // Token expired or invalid, redirect to login
        navigate('/login', { 
          state: { 
            from: {
              pathname: `/tours/${tourId}/book`,
              search: location.search
            },
            message: 'Your session has expired. Please log in again.'
          } 
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">Book Your Tour</h2>
      {tour && (
        <div className="mb-6 p-4 bg-gray-50 rounded">
          <h3 className="text-xl font-semibold">{tour.name}</h3>
          <p className="text-gray-600">{tour.duration} days • {tour.maxGroupSize} people max</p>
          <p className="text-lg font-medium mt-2">₹{tour.price} per person</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tour Date *
              </label>
              <input
                type="date"
                name="tourDate"
                value={formData.tourDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of People *
              </label>
              <input
                type="number"
                name="numberOfPeople"
                min="1"
                max={tour?.maxGroupSize || 20}
                value={formData.numberOfPeople}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Number *
              </label>
              <input
                type="tel"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Special Requests
            </label>
            <textarea
              name="specialRequests"
              rows="5"
              value={formData.specialRequests}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Any special requirements or requests..."
            />
          </div>
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <span className="text-lg font-medium">Total:</span>
            <span className="text-2xl font-bold">${totalPrice}</span>
          </div>
          
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {submitting ? 'Processing...' : 'Book Now'}
          </button>
          
          <p className="mt-3 text-sm text-gray-500">
            * Required fields. Your booking will be confirmed after payment.
          </p>
        </div>
      </form>
    </div>
  );
};

export default BookingForm;
